import { WindInfo } from '../map/WorldSource';

/**
 * Pogoda: ile chmur i którędy idą.
 *
 * **Zegar jest serwera, tak jak przy dobie.** Dwóch graczy stojących obok siebie
 * musi widzieć to samo niebo, więc zachmurzenie jest **czystą funkcją zegara
 * świata i ziarna wziętego z nazwy świata** — obu klient dostaje w powitaniu.
 * Nie ma tu nic do wysyłania w kółko i nic, co restart serwera by przesunął.
 *
 * Formuła żyje **wyłącznie po tej stronie** i to nie jest dług: serwer nie czyta
 * pogody, bo żadna reguła gry o niej nie wie. Bliźniacza reguła powstaje wtedy,
 * gdy dwie strony muszą zgodzić się co do liczby — a tu zgadzać się muszą tylko
 * dwaj gracze, czyli dwa klienty tego samego builda.
 *
 * **Kierunek jest z generatora, przez manifest.** Pasy wiatrów decydują o
 * cieniu opadowym całej mapy i przyjeżdżają jako dane; klient dokłada do nich
 * tylko własną szerokość geograficzną.
 */

/** Ile sekund realnych zajmuje pełny obrót pogody od czystego nieba do gęstego. */
const WEATHER_PERIOD = 45 * 60;

/** Najmniejsze i największe zachmurzenie, żeby nigdy nie było ani pusto, ani szczelnie. */
const COVER_MIN = 0.12;
const COVER_MAX = 0.78;

/**
 * Od jakiego zachmurzenia zaczyna kropić i przy jakim leje.
 *
 * **0,70, nie 0,80.** Sufit zachmurzenia to 0,78, a pomiar na sześciu godzinach
 * zegara dał 0,28–0,75 — próg na osiemdziesiątce znaczyłby „deszcz nie pada
 * nigdy", poza wymuszeniem z panelu. 0,70 to górna ćwiartka tego, co ta pogoda
 * naprawdę robi, i tyle też ma znaczyć: pada w szczytach fali, a nie w tle.
 *
 * Górny koniec siada tuż pod sufitem, żeby ulewa była osiągalna, a nie
 * teoretyczna — przy 0,75, czyli najgęstszym niebie z pomiaru, natężenie
 * wychodzi 0,96.
 */
const RAIN_FROM = 0.70;
const RAIN_TO = 0.76;

/** Jak szybko chmury płyną, w jednostkach świata na sekundę. */
const DRIFT_SPEED = 0.55;

export interface WeatherState {
  /** Zachmurzenie od 0 do 1. */
  cover: number;
  /** Przesunięcie pola szumu w jednostkach świata. */
  offsetX: number;
  offsetZ: number;
  /** Ile pada, od 0 do 1. Czysta funkcja zachmurzenia — patrz `rainAt`. */
  rain: number;
  /** Dokąd wieje: kierunek znoszenia chmur, a więc i skosu deszczu. */
  slantX: number;
  slantZ: number;
}

/** Ziarno z nazwy świata: dwa światy nie mają mieć tej samej pogody. */
function seedOf(worldId: string): number {
  let hash = 2166136261;
  for (let i = 0; i < worldId.length; i += 1) {
    hash ^= worldId.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0) / 4294967295;
}

/**
 * Ile pada przy takim zachmurzeniu.
 *
 * Łagodnie, a nie skokiem na progu: przy 0,71 mży, przy 0,76 leje. Próg
 * włączający dwa tysiące smug w jednej klatce czyta się jak błąd, a nie jak
 * pogoda.
 */
export function rainAt(cover: number): number {
  const t = Math.min(1, Math.max(0, (cover - RAIN_FROM) / (RAIN_TO - RAIN_FROM)));
  return t * t * (3 - 2 * t);
}

/** Gładka wartość szumu na jednym wymiarze, z okresem 1. */
function wave(t: number): number {
  const i = Math.floor(t);
  const f = t - i;
  const smooth = f * f * (3 - 2 * f);
  const at = (k: number): number => {
    const x = Math.sin(k * 127.1 + 311.7) * 43758.5453123;
    return x - Math.floor(x);
  };
  return at(i) * (1 - smooth) + at(i + 1) * smooth;
}

export class Weather {
  /** Przesunięcie narastające całkowaniem, nie liczone z zegara. */
  private offsetX = 0;
  private offsetZ = 0;
  private readonly seed: number;
  /**
   * Zachmurzenie wymuszone z panelu, albo `undefined`.
   *
   * Trzymane osobno od krzywej, a nie wstrzykiwane w nią: „ustawione ręcznie"
   * i „akurat tyle wyszło" to dwa różne stany i tylko pierwszy da się cofnąć.
   */
  private pinned?: number;

  constructor(
    worldId: string,
    private readonly wind: WindInfo | undefined
  ) {
    this.seed = seedOf(worldId) * 1000;
  }

  /** Przypnij zachmurzenie albo oddaj je automatowi (`undefined`). */
  pin(cover: number | undefined): void {
    this.pinned = cover === undefined ? undefined : Math.min(1, Math.max(0, cover));
  }

  /**
   * Zachmurzenie o danej chwili zegara świata.
   *
   * Dwie fale o niewspółmiernych okresach, żeby pogoda nie wracała do tego
   * samego stanu w regularnym rytmie — jedna fala daje wahadło, które po
   * kilkunastu minutach zaczyna być rozpoznawalne. Przypięte z panelu wygrywa
   * z krzywą i nic jej przy tym nie przestawia — puszczenie wraca dokładnie
   * tam, gdzie pogoda i tak by doszła.
   */
  coverAt(clock: number): number {
    if (this.pinned !== undefined) return this.pinned;
    const t = clock / WEATHER_PERIOD + this.seed;
    const slow = wave(t);
    const slower = wave(t * 0.37 + 11.5);
    return COVER_MIN + (COVER_MAX - COVER_MIN) * (slow * 0.65 + slower * 0.35);
  }

  /**
   * Którędy wieje na tej szerokości: −1 ze wschodu, +1 z zachodu.
   *
   * Pasy przychodzą z serwera, bo to one decydują o klimacie mapy. Samą
   * szerokość klient czyta **z własnego położenia i zasięgu świata**, więc przy
   * granicy pasa może się o ułamek procenta rozminąć z generatorem. Pasy są
   * szerokie na trzecią część drogi do bieguna; nie ma tam czego rozminąć.
   */
  directionAt(worldZ: number, worldHalfDepth: number): number {
    if (!this.wind || this.wind.bands.length === 0) return 1;

    const fromEquator = Math.min(
      1,
      (Math.abs(worldZ) / Math.max(worldHalfDepth, 1e-6)) * this.wind.latitudeSpan
    );
    for (const band of this.wind.bands) {
      if (fromEquator < band.until) return band.direction;
    }
    return this.wind.bands[this.wind.bands.length - 1].direction;
  }

  /**
   * Pchnij chmury o jedną klatkę.
   *
   * Przesunięcie jest **całkowane**, a nie liczone z zegara razy prędkość:
   * kierunek zmienia się, gdy gracz przechodzi między pasami, a wzór z zegara
   * przeskoczyłby wtedy całą historię ruchu na nowy kierunek i chmury
   * teleportowałyby się w bok.
   *
   * Znos poprzeczny jest powolny i wzięty z tej samej fali co pogoda — wiatr
   * generatora jest czysto równoleżnikowy, a chmury idące dokładnie na wschód
   * wyglądają jak przesuwana tapeta.
   */
  update(deltaTime: number, clock: number, worldZ: number, worldHalfDepth: number): WeatherState {
    const direction = this.directionAt(worldZ, worldHalfDepth);
    const sway = (wave(clock / (WEATHER_PERIOD * 1.7) + this.seed) - 0.5) * 0.5;

    this.offsetX -= direction * DRIFT_SPEED * deltaTime;
    this.offsetZ -= sway * DRIFT_SPEED * deltaTime;

    // Przesunięcie maleje, więc pole szumu jedzie po świecie w drugą stronę —
    // chmury idą w `+direction` i tam samo znosi deszcz.
    const cover = this.coverAt(clock);
    return {
      cover,
      offsetX: this.offsetX,
      offsetZ: this.offsetZ,
      rain: rainAt(cover),
      slantX: direction,
      slantZ: sway,
    };
  }
}
